/**
 * **Holdout** (Core p.272, 6-round cap, single CZ) — PCs start at **4 points** (a
 * scoring-formula CONSTANT, not stored state — computed at read time, never
 * persisted) and lose 1 per enemy character inside the CZ at the end of round 6. PC
 * win iff the resulting score ≥ 1, else enemy win. No draw case in RAW.
 *
 * Composes `unitsInZone` (kind-agnostic occupancy) filtered to `isCharacter` +
 * NPC-side — matching `unitsInZone`/`controlOf`'s own documented "no liveness/HP
 * filter" default: **a wrecked enemy mech still occupies the CZ by position alone**,
 * because RAW calls for no such filter.
 */
import { isCharacter, sideOf, type EncounterState, type SitrepConfig } from "@lancer-vtt/domain";
import { unitsInZone } from "../zones";
import type { SitrepStatus } from "../status";
import { roundLimitReached, roundsRemainingFor } from "../roundBoundary";

/** RAW's Holdout starting score (Core p.272) — PCs start at 4, lose 1 per enemy character in the CZ. */
const HOLDOUT_STARTING_SCORE = 4;

export function holdoutStatus(state: EncounterState, sitrep: SitrepConfig): SitrepStatus {
  const [zone] = sitrep.zones;
  const enemyCount = zone ? unitsInZone(state, zone).filter((c) => isCharacter(c) && sideOf(c) === "npc").length : 0;
  const pcScore = HOLDOUT_STARTING_SCORE - enemyCount;
  const roundsRemaining = roundsRemainingFor(state, sitrep.roundLimit);
  if (!roundLimitReached(state, sitrep.roundLimit)) {
    return { phase: "active", roundsRemaining };
  }
  return { phase: "decided", winner: pcScore >= 1 ? "pc" : "npc" };
}
